import {
  Alert as MuiAlert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from '@mui/material'
import { MoreVert as MoreVertIcon, Refresh as RefreshIcon, Info as InfoIcon } from '@mui/icons-material'
import { FC, useEffect, useRef, useState } from 'react'
import { ContentHead } from '../../../components/common/ContentHead'
import { CardBox } from '../../../theme/styleComponents'
import { EmptyData } from '../../../components/common/EmptyData'
import { Alert } from '../../../components/common/Alert'
import { ConfirmationModal } from '../../../components/common/ConfirmationModal'
import AttorneyDropdown from '../../../components/common/AttorneyDropdown'
import MatterDropdown from '../../../components/common/MatterDropdown'
import { PaginationWithJump } from '../../../components/common/PaginationWithJump'
import { formatDateTime } from '../../../utils'
import {
  Case,
  Maybe,
  User,
  BillEmailBatchItemType,
  PrintJobType,
  PrintJobSortBy,
  PrintJobSortDir,
  useCreatePrintJobMutation,
  useCancelPrintJobMutation,
  useRetryPrintJobMutation,
  useFetchPrintJobsLazyQuery,
  useDownloadPrintJobLazyQuery,
  useGetBillEmailBatchesLazyQuery,
  useGetBillEmailBatchLazyQuery,
  useToggleBillEmailBatchItemExclusionMutation,
  BillStatus,
  BillDeliveryMethod,
  BillingType,
  BillingCycle,
  DelinquentPaymentStatus,
  PrintJobTrustScope
} from '../../../../generated'
import { GRAPHQL_QUERY_POLICY, COLL1_ATTORNEY_ID, COLL2_ATTORNEY_ID } from '../../../constants'
import palette from '../../../theme/palette'
import { Link } from 'react-router-dom'
import { gql, useMutation } from '@apollo/client'
import { statusColors } from '../../../utils/billing'

const MARK_PRINT_JOB_DELIVERED = gql`
  mutation MarkPrintJobBillsAsDelivered($id: String!) {
    markPrintJobBillsAsDelivered(id: $id) {
      printJob {
        id
      }
      response {
        status
        message
      }
    }
  }
`

const PRINT_JOB_STATUS_COLORS: Record<string, string> = {
  REQUESTED: '#9E9E9E',
  IN_PROGRESS: '#0288D1',
  COMPLETED: '#2E7D32',
  FAILED: '#D32F2F',
  CANCELLED: '#757575'
}

const PRINT_JOB_TYPE_LABELS: Record<string, string> = {
  PRE_BILLS: 'Pre Bills',
  DELINQUENT_BILLS: 'Delinquent Bills',
  NEW_BILLS: 'New Bills',
  BILLS: 'All Bills',
  CUSTOM: 'Custom',
  BY_MATTER: 'By Matter',
  PRINT_BATCH: 'Print Batch'
}

/** Human-readable logic description per print job type (for tooltip and confirmation) */
const PRINT_JOB_TYPE_DESCRIPTIONS: Record<string, string> = {
  PRE_BILLS: 'Includes pre-bills only: draft, sent for approval, or awaiting department head approval.',
  DELINQUENT_BILLS:
    'One current statement for every matter carrying an unpaid balance more than 30 days old — counting the balance on earlier bills that were rolled forward into the current one, not just the current bill\u2019s own due date. Matters whose only unpaid balance is newer than 30 days are still in their grace period and are excluded. Collection matters (Resp1 Attorney Coll1 or Coll2) are excluded too, unless Coll1 or Coll2 is picked as the Custom Attorney.',
  NEW_BILLS:
    'Includes bills that are billed, partially paid, or sent to client, with due date within the last 5 days or in the future.',
  BILLS:
    'Includes every bill that is not paid to date, whatever its due date — including bills that are past due but still inside the 30-day grace period, which neither New Bills nor Delinquent Bills covers.',
  BY_MATTER:
    'Includes all bills for the selected matter that are billed, sent to client, partially paid, paid, or forwarded. Excludes voided and pre-bills.',
  PRINT_BATCH: 'Includes bills for clients on paper delivery from the selected email batch.'
}

const RADIO_TO_ENUM: Record<string, PrintJobType> = {
  'pre-bills': PrintJobType.PreBills,
  'new-bills': PrintJobType.NewBills,
  'delinquent-bills': PrintJobType.DelinquentBills,
  coll1: PrintJobType.Bills,
  coll2: PrintJobType.Bills,
  'by-matter': PrintJobType.ByMatter,
  'print-batch': PrintJobType.PrintBatch,
  'potential-bills': PrintJobType.PotentialBills
}

/**
 * Billing cycles for "Bills To Be Generated". These set the entry cutoff exactly as a real bill run
 * would: mid-month bills everything up to the 15th, end-of-month everything up to the last day of
 * last month, custom up to a date you pick.
 */
const BILLING_CYCLE_OPTIONS = [
  { label: 'Mid of Month (up to the 15th)', value: BillingCycle.MidOfMonth },
  { label: 'End of Month (up to the last day of last month)', value: BillingCycle.EndOfMonth },
  { label: 'Custom Date', value: BillingCycle.CustomDate }
]

const CUSTOM_ATTORNEY_BILL_TYPE_OPTIONS = [
  { label: 'Pre Bills', value: PrintJobType.PreBills },
  { label: 'All Bills', value: PrintJobType.Bills },
  { label: 'New Bills', value: PrintJobType.NewBills },
  { label: 'Delinquent Bills', value: PrintJobType.DelinquentBills }
]

/** Attorney IDs for collection print job options */
const RADIO_TO_ATTORNEY_IDS: Record<string, string[] | undefined> = {
  coll1: [COLL1_ATTORNEY_ID],
  coll2: [COLL2_ATTORNEY_ID]
}

/**
 * Role the Print Batch attorney filter matches on. Fixed to Responsible Attorney 1 — the batch
 * filter answers "print this batch for one attorney", which is the Resp1 assignment; the Custom
 * Attorney option below is where any other role can be picked.
 */
const PRINT_BATCH_ATTORNEY_ROLE = 'responsibleAttorney1'

/**
 * Print Batch trust filter. "Trust money involved" is the same test the /billing/bills
 * "Bills with Trust Money" checkbox uses — the matter's trust balance across its parties is above
 * zero right now — so a biller who filters that list and then prints the batch gets the same set.
 * It is a snapshot of what the matter holds today, not a record of trust ever applied to a bill.
 */
const TRUST_SCOPE_OPTIONS = [
  { label: 'All Bills', value: PrintJobTrustScope.All },
  { label: 'With Trust Money', value: PrintJobTrustScope.WithTrust },
  { label: 'Without Trust Money', value: PrintJobTrustScope.WithoutTrust }
]

const TRUST_SCOPE_LABELS: Record<string, string> = {
  [PrintJobTrustScope.All]: 'All bills',
  [PrintJobTrustScope.WithTrust]: 'With trust money',
  [PrintJobTrustScope.WithoutTrust]: 'Without trust money'
}

/** Sentence appended to the confirmation copy for a trust-filtered batch. */
function formatTrustScopeNote(trustScope: PrintJobTrustScope): string {
  if (trustScope === PrintJobTrustScope.WithTrust) {
    return ' Only bills whose matter currently holds trust money are included.'
  }
  if (trustScope === PrintJobTrustScope.WithoutTrust) {
    return ' Only bills whose matter holds no trust money are included.'
  }
  return ''
}

const ATTORNEY_ROLE_OPTIONS = [
  { label: 'Responsible Attorney 1', value: 'responsibleAttorney1' },
  { label: 'Responsible Attorney 2', value: 'responsibleAttorney2' },
  { label: 'Responsible Attorney 3', value: 'responsibleAttorney3' },
  { label: 'Responsible Attorney 4', value: 'responsibleAttorney4' },
  { label: 'Responsible Attorney 5', value: 'responsibleAttorney5' },
  { label: 'Responsible Attorney 6', value: 'responsibleAttorney6' },
  { label: 'Paralegal', value: 'paralegal' },
  { label: 'Investigator', value: 'investigator' },
  { label: 'Any Role', value: 'any' }
]

/**
 * Bill delivery methods a Delinquent Bills job can be widened to. Every other job type is
 * Paper-only. Selecting all three drops the delivery-method filter entirely on the API side, so
 * matters whose parties have no delivery method set are printed as well.
 */
const DELIVERY_METHOD_OPTIONS = [
  { label: 'Paper', value: BillDeliveryMethod.Paper },
  { label: 'Email', value: BillDeliveryMethod.Email },
  { label: 'SMS', value: BillDeliveryMethod.Text }
]

const DELIVERY_METHOD_LABELS: Record<string, string> = {
  [BillDeliveryMethod.Paper]: 'Paper',
  [BillDeliveryMethod.Email]: 'Email',
  [BillDeliveryMethod.Text]: 'SMS'
}

/**
 * Matter billing types a Delinquent Bills job can be narrowed to. Flat Fee is the default because
 * that is the debt the firm chases on this run; the other types are opt-in. Leaving the API field
 * unset means no billing-type restriction, so the default here is what makes the job flat-fee only.
 */
const BILLING_TYPE_OPTIONS = [
  { label: 'Flat Fee', value: BillingType.FlatFee },
  { label: 'Hourly', value: BillingType.Hourly },
  { label: 'Hybrid', value: BillingType.Hybrid },
  { label: 'Contingency', value: BillingType.Contingency },
  { label: 'Contingency Hybrid', value: BillingType.ContingencyHybrid },
  { label: 'Pro Bono', value: BillingType.ProBono }
]

const BILLING_TYPE_LABELS: Record<string, string> = BILLING_TYPE_OPTIONS.reduce(
  (acc, o) => ({ ...acc, [o.value]: o.label }),
  {} as Record<string, string>
)

/** Every billing type selected = no billing-type restriction on the API side. */
const isAllBillingTypes = (types: BillingType[]): boolean => BILLING_TYPE_OPTIONS.every(o => types.includes(o.value))

/**
 * How much of the aged debt has been paid. "Fully Unpaid" means the matter has paid nothing at all
 * against its overdue bills. A client who has put money against any of them is engaging, and is
 * only included when "Partially Unpaid" is ticked as well. Selecting both is the set this job has
 * always printed.
 */
const PAYMENT_STATUS_OPTIONS = [
  { label: 'Fully Unpaid', value: DelinquentPaymentStatus.FullyUnpaid },
  { label: 'Partially Unpaid', value: DelinquentPaymentStatus.PartiallyUnpaid }
]

const PAYMENT_STATUS_LABELS: Record<string, string> = PAYMENT_STATUS_OPTIONS.reduce(
  (acc, o) => ({ ...acc, [o.value]: o.label }),
  {} as Record<string, string>
)

const isAllPaymentStatuses = (statuses: DelinquentPaymentStatus[]): boolean =>
  PAYMENT_STATUS_OPTIONS.every(o => statuses.includes(o.value))

/** Every method selected = no delivery-method restriction (see DELIVERY_METHOD_OPTIONS). */
const isAllDeliveryMethods = (methods: BillDeliveryMethod[]): boolean =>
  DELIVERY_METHOD_OPTIONS.every(o => methods.includes(o.value))

/** Human-readable delivery scope, used in the confirmation copy and the history row caption. */
function formatDeliveryScope(
  methods: BillDeliveryMethod[],
  includeProformaNotes: boolean,
  billingTypes: BillingType[] = [],
  paymentStatuses: DelinquentPaymentStatus[] = []
): string {
  const scope = isAllDeliveryMethods(methods)
    ? 'All delivery methods, including matters with no delivery method set.'
    : methods.length
    ? `Delivery method: ${methods.map(m => DELIVERY_METHOD_LABELS[m] ?? m).join(', ')}.`
    : ''
  const types =
    billingTypes.length && !isAllBillingTypes(billingTypes)
      ? ` Billing type: ${billingTypes.map(t => BILLING_TYPE_LABELS[t] ?? t).join(', ')}.`
      : billingTypes.length
      ? ' All billing types.'
      : ''
  const payment =
    paymentStatuses.length && !isAllPaymentStatuses(paymentStatuses)
      ? ` Payment status: ${paymentStatuses.map(p => PAYMENT_STATUS_LABELS[p] ?? p).join(', ')}.`
      : ''
  const notes = includeProformaNotes ? ' Performa (billing) notes are printed on each bill.' : ''
  return `${scope}${types}${payment}${notes}`
}

/** Sort-by values for create print job (must match API CreatePrintJobInput) */
const SORT_BY = { CaseNumber: 'CASE_NUMBER', Attorney: 'ATTORNEY', DueDate: 'DUE_DATE' } as const
/** Sort-direction values for create print job */
const SORT_DIR = { Asc: 'ASC', Desc: 'DESC' } as const

const SORT_BY_LABEL: Record<string, string> = {
  CASE_NUMBER: 'Case number',
  ATTORNEY: 'Attorney name',
  DUE_DATE: 'Due date'
}
const SORT_DIR_LABEL: Record<string, string> = { ASC: 'Asc', DESC: 'Desc' }

function formatSortOrder(sortBy?: string | null, sortDir?: string | null): string {
  if (!sortBy && !sortDir) return '—'
  const by = sortBy ? SORT_BY_LABEL[sortBy] ?? sortBy : ''
  const dir = sortDir ? SORT_DIR_LABEL[sortDir] ?? sortDir : ''
  return [by, dir].filter(Boolean).join(', ') || '—'
}

/** Resolve a human-readable type label, accounting for attorney-filtered variants */
function getPrintJobTypeLabel(
  printJobType: string | null | undefined,
  attorneyIds: string[] | null | undefined
): string {
  // Batch jobs keep their own label even when attorney-filtered — the Attorney column already
  // names who they were narrowed to, and a Coll1/Coll2 pick there is a filter, not a Coll job.
  const isBatchType = printJobType === 'PRINT_BATCH' || printJobType === 'PRINT_BATCH_ALL'
  if (attorneyIds?.length && !isBatchType) {
    const [id] = attorneyIds
    if (id === COLL1_ATTORNEY_ID) return 'Coll1'
    if (id === COLL2_ATTORNEY_ID) return 'Coll2'
    if (printJobType === 'DELINQUENT_BILLS') return 'Custom Attorney'
  }
  return PRINT_JOB_TYPE_LABELS[printJobType ?? ''] ?? printJobType ?? '—'
}

/** Filename slug per print job type for download */
const PRINT_JOB_DOWNLOAD_SLUG: Record<string, string> = {
  PRE_BILLS: 'prebills',
  DELINQUENT_BILLS: 'delinquent-bills',
  NEW_BILLS: 'new-bills',
  CUSTOM: 'custom',
  PRINT_BATCH: 'print-batch'
}

function getPrintJobDownloadFilename(printJobType: string, date: Date): string {
  const slug = PRINT_JOB_DOWNLOAD_SLUG[printJobType ?? ''] ?? 'bills'
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  const yyyy = date.getFullYear()
  return `${slug}-${mm}-${dd}-${yyyy}.pdf`
}

/** Format duration between two dates as "Xh Ym" or "Xm" or "&lt;1m" */
function formatDurationMs(ms: number): string {
  if (ms < 0 || !Number.isFinite(ms)) return '—'
  const totalMinutes = Math.floor(ms / 60_000)
  if (totalMinutes < 1) return '<1m'
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (hours > 0) return `${hours}h ${minutes}m`
  return `${minutes}m`
}

const POLL_INTERVAL_MS = 15000

export type PrintJobStatusFilter = 'in_progress_completed' | 'all' | 'failed' | 'cancelled'

function getFetchPrintJobsInput(
  page: number,
  limit: number,
  statusFilter: PrintJobStatusFilter
): {
  paginationOptions: { page: number; limit: number }
  status?: string
  statuses?: string[]
} {
  const paginationOptions = { page, limit }
  switch (statusFilter) {
    case 'in_progress_completed':
      return { paginationOptions, statuses: ['REQUESTED', 'IN_PROGRESS', 'COMPLETED'] }
    case 'all':
      return {
        paginationOptions,
        statuses: ['REQUESTED', 'IN_PROGRESS', 'COMPLETED', 'FAILED', 'CANCELLED']
      }
    case 'failed':
      return { paginationOptions, status: 'FAILED' }
    case 'cancelled':
      return { paginationOptions, status: 'CANCELLED' }
    default:
      return { paginationOptions, statuses: ['REQUESTED', 'IN_PROGRESS', 'COMPLETED'] }
  }
}

export const PrintJobs: FC = (): JSX.Element => {
  const [printJobType, setPrintJobType] = useState<string>('pre-bills')
  const [customAttorneyId, setCustomAttorneyId] = useState<string | null>(null)
  const [attorneyRole, setAttorneyRole] = useState<string>('responsibleAttorney1')
  const [customBillType, setCustomBillType] = useState<PrintJobType>(PrintJobType.Bills)
  /**
   * Delinquent Bills only. Defaults to Paper, which is what this page has always printed; the
   * owner widens it when he wants to review every unpaid bill regardless of how the client is
   * billed. Reset whenever the job type changes so a widened scope is never carried over unseen.
   */
  const [deliveryMethods, setDeliveryMethods] = useState<BillDeliveryMethod[]>([BillDeliveryMethod.Paper])
  const [billingTypes, setBillingTypes] = useState<BillingType[]>([BillingType.FlatFee])
  const [paymentStatuses, setPaymentStatuses] = useState<DelinquentPaymentStatus[]>([
    DelinquentPaymentStatus.FullyUnpaid
  ])
  const [includeProformaNotes, setIncludeProformaNotes] = useState(false)
  /** "Bills To Be Generated" — at least one attorney is mandatory (see the gate on the button). */
  const [potentialAttorneyIds, setPotentialAttorneyIds] = useState<string[]>([])
  const [potentialBillingCycle, setPotentialBillingCycle] = useState<BillingCycle>(BillingCycle.EndOfMonth)
  const [potentialCustomDate, setPotentialCustomDate] = useState<string>('')
  /**
   * Off by default: the preview then matches the attorney bill run this page triggers. Ticking it
   * matches the scheduled monthly run, which raises a $0 statement to re-state what a matter owes.
   */
  const [includeZeroDollarBills, setIncludeZeroDollarBills] = useState(false)
  const [selectedMatterId, setSelectedMatterId] = useState<string | null>(null)
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null)
  // Optional narrowing of a batch to a single Responsible Attorney 1 — e.g. print August's
  // end-of-month bills for Jarred only.
  const [batchAttorneyId, setBatchAttorneyId] = useState<string | null>(null)
  /** Display name of the attorney above, for the confirmation copy and preview header. */
  const [batchAttorneyName, setBatchAttorneyName] = useState<string>('')
  /**
   * Print Batch only: whether to print the batch's bills whose matter holds trust money, only
   * those whose matter does not, or every bill. Defaults to All, which is what this option has
   * always printed.
   */
  const [trustScope, setTrustScope] = useState<PrintJobTrustScope>(PrintJobTrustScope.All)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [fetchBatches, { data: batchesData, loading: batchesLoading }] = useGetBillEmailBatchesLazyQuery({
    fetchPolicy: 'network-only'
  })
  const [fetchPaperItems, { data: paperItemsData, loading: paperItemsLoading, error: paperItemsError }] =
    useGetBillEmailBatchLazyQuery({ fetchPolicy: 'network-only' })
  /**
   * Paper items for the selected batch, narrowed by the attorney filter when one is set, so the
   * preview list and the "N bill(s) will be printed" count show exactly what the job will print.
   */
  const getPaperItemsInput = (batchId: number) => ({
    batchId,
    type: BillEmailBatchItemType.Paper,
    ...(batchAttorneyId ? { attorneyIds: [batchAttorneyId], attorneyRole: PRINT_BATCH_ATTORNEY_ROLE } : {}),
    ...(trustScope !== PrintJobTrustScope.All ? { trustScope } : {})
  })
  const [toggleExclusion, { loading: toggling }] = useToggleBillEmailBatchItemExclusionMutation({
    onError: e => Alert.error(e.message),
    onCompleted: () => {
      if (selectedBatchId) {
        fetchPaperItems({ variables: { input: getPaperItemsInput(selectedBatchId) } })
      }
    }
  })
  const [sortBy, setSortBy] = useState<string>(SORT_BY.CaseNumber)
  const [sortDir, setSortDir] = useState<string>(SORT_DIR.Desc)
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState<PrintJobStatusFilter>('in_progress_completed')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false)
  const [cancelJobId, setCancelJobId] = useState<string | null>(null)
  const [retryConfirmOpen, setRetryConfirmOpen] = useState(false)
  const [retryJobId, setRetryJobId] = useState<string | null>(null)
  const [markDeliverConfirmOpen, setMarkDeliverConfirmOpen] = useState(false)
  const [markDeliverJobId, setMarkDeliverJobId] = useState<string | null>(null)
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null)
  const [actionMenuJobId, setActionMenuJobId] = useState<string | null>(null)
  const [failedErrorDialog, setFailedErrorDialog] = useState<{ message: string } | null>(null)
  const pendingDownloadFilenameRef = useRef<string | null>(null)
  const [createPrintJob, { loading: creating }] = useCreatePrintJobMutation({
    onError: e => Alert.error(e.message),
    onCompleted: data => {
      const msg = data?.createPrintJob?.response?.message
      if (msg) Alert.success(msg)
      setConfirmOpen(false)
      refetch()
    }
  })

  /**
   * The delivery-method and Performa Notes options apply to delinquent bills only — either the
   * Delinquent Bills radio, or Custom Attorney narrowed to that bill type.
   */
  const isDelinquentSelection =
    printJobType === 'delinquent-bills' ||
    (printJobType === 'custom-attorney' && customBillType === PrintJobType.DelinquentBills)

  /**
   * "Bills To Be Generated" is a preview: it runs the same computation a bill run would and renders
   * the resulting pre-bills, but creates nothing. It needs its own attorney, cycle and filter set.
   */
  const isPotentialBills = printJobType === 'potential-bills'
  const potentialDateMissing =
    isPotentialBills && potentialBillingCycle === BillingCycle.CustomDate && !potentialCustomDate

  const limit = 20
  const [fetchPrintJobs, { data, loading, refetch }] = useFetchPrintJobsLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: {
      input: getFetchPrintJobsInput(page, limit, statusFilter)
    }
  })

  const [downloadPrintJob, { loading: downloading }] = useDownloadPrintJobLazyQuery({
    fetchPolicy: 'network-only',
    onError: e => Alert.error(e.message),
    onCompleted: res => {
      const url = res?.downloadPrintJob?.preSignedUrl
      if (url) {
        const filename = pendingDownloadFilenameRef.current || 'bills.pdf'
        pendingDownloadFilenameRef.current = null
        triggerDownload(url, filename)
      }
    }
  })

  const [cancelPrintJob, { loading: cancelling }] = useCancelPrintJobMutation({
    onError: e => Alert.error(e.message),
    onCompleted: data => {
      const msg = data?.cancelPrintJob?.response?.message
      if (msg) Alert.success(msg)
      refetch()
    }
  })

  const [retryPrintJob, { loading: retrying }] = useRetryPrintJobMutation({
    onError: e => Alert.error(e.message),
    onCompleted: data => {
      const msg = data?.retryPrintJob?.response?.message
      if (msg) Alert.success(msg)
      refetch()
    }
  })

  const [markPrintJobDelivered, { loading: markingDelivered }] = useMutation(MARK_PRINT_JOB_DELIVERED, {
    onError: e => Alert.error(e.message),
    onCompleted: data => {
      const msg = data?.markPrintJobBillsAsDelivered?.response?.message
      Alert.success(msg || 'Bills marked as delivered to client')
      refetch()
    }
  })

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    fetchPrintJobs({
      variables: { input: getFetchPrintJobsInput(page, limit, statusFilter) }
    })
  }, [page, statusFilter, fetchPrintJobs])

  useEffect(() => {
    const printJobs = data?.fetchPrintJobs?.printJobs ?? []
    const needsPolling = printJobs.some(pj => pj?.status === 'IN_PROGRESS' || pj?.status === 'REQUESTED')
    if (needsPolling) {
      pollRef.current = setInterval(() => {
        refetch?.()
      }, POLL_INTERVAL_MS)
    } else if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [data?.fetchPrintJobs?.printJobs, refetch])

  useEffect(() => {
    if (selectedBatchId && printJobType === 'print-batch') {
      fetchPaperItems({ variables: { input: getPaperItemsInput(selectedBatchId) } })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBatchId, printJobType, batchAttorneyId, trustScope])

  const paperItems = paperItemsData?.getBillEmailBatch?.batch?.items ?? []
  const paperBatch = paperItemsData?.getBillEmailBatch?.batch
  const activePaperCount = paperItems.filter(i => i.status !== 'EXCLUDED').length
  const excludedCount = paperItems.filter(i => i.status === 'EXCLUDED').length

  const triggerDownload = async (preSignedUrl: string, filename: string = 'bills.pdf') => {
    try {
      const response = await fetch(preSignedUrl)
      if (!response.ok) {
        Alert.error('Download failed')
        return
      }
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch {
      Alert.error('Download failed')
    }
  }

  const handleRequestPrint = () => {
    const isCustomAttorney = printJobType === 'custom-attorney'
    const isByMatter = printJobType === 'by-matter'
    const isPrintBatch = printJobType === 'print-batch'
    const enumType = isCustomAttorney ? customBillType : RADIO_TO_ENUM[printJobType] ?? PrintJobType.PreBills
    let attorneyIds = RADIO_TO_ATTORNEY_IDS[printJobType]
    if (isCustomAttorney && customAttorneyId) {
      attorneyIds = [customAttorneyId]
    }
    if (isPrintBatch && batchAttorneyId) {
      attorneyIds = [batchAttorneyId]
    }
    if (isPotentialBills) {
      attorneyIds = potentialAttorneyIds
    }
    createPrintJob({
      variables: {
        input: {
          printJobType: enumType,
          sortBy: sortBy as PrintJobSortBy,
          sortDir: sortDir as PrintJobSortDir,
          ...(attorneyIds ? { attorneyIds } : {}),
          ...(isCustomAttorney ? { attorneyRole } : {}),
          ...(isPrintBatch && batchAttorneyId ? { attorneyRole: PRINT_BATCH_ATTORNEY_ROLE } : {}),
          ...(isByMatter && selectedMatterId ? { caseId: selectedMatterId } : {}),
          ...(isPrintBatch && selectedBatchId ? { batchId: selectedBatchId } : {}),
          ...(isPrintBatch && trustScope !== PrintJobTrustScope.All ? { trustScope } : {}),
          ...(isDelinquentSelection ? { deliveryMethods, billingTypes, paymentStatuses, includeProformaNotes } : {}),
          ...(isPotentialBills
            ? {
                billingCycle: potentialBillingCycle,
                ...(potentialBillingCycle === BillingCycle.CustomDate ? { customDate: potentialCustomDate } : {}),
                // Only send the narrowing filters when the user actually narrowed: an empty
                // selection means "no restriction", not "match nothing".
                ...(billingTypes.length ? { billingTypes } : {}),
                ...(deliveryMethods.length ? { deliveryMethods } : {}),
                includeZeroDollarBills
              }
            : {})
        }
      }
    })
  }

  const handleCancelClick = (id: string) => {
    setActionMenuAnchor(null)
    setActionMenuJobId(null)
    setCancelJobId(id)
    setCancelConfirmOpen(true)
  }

  const handleCancelConfirm = () => {
    if (cancelJobId) {
      cancelPrintJob({ variables: { id: cancelJobId } })
      setCancelJobId(null)
      setCancelConfirmOpen(false)
    }
  }

  const handleRetryClick = (id: string) => {
    setActionMenuAnchor(null)
    setActionMenuJobId(null)
    setRetryJobId(id)
    setRetryConfirmOpen(true)
  }

  const handleRetryConfirm = () => {
    if (retryJobId) {
      retryPrintJob({ variables: { id: retryJobId } })
      setRetryJobId(null)
      setRetryConfirmOpen(false)
    }
  }

  const handleMarkDeliverClick = (id: string) => {
    setActionMenuAnchor(null)
    setActionMenuJobId(null)
    setMarkDeliverJobId(id)
    setMarkDeliverConfirmOpen(true)
  }

  const handleMarkDeliverConfirm = () => {
    if (markDeliverJobId) {
      markPrintJobDelivered({ variables: { id: markDeliverJobId } })
      setMarkDeliverJobId(null)
      setMarkDeliverConfirmOpen(false)
    }
  }

  const pagination = data?.fetchPrintJobs?.pagination
  const printJobs = data?.fetchPrintJobs?.printJobs ?? []
  const totalPages = pagination?.totalPages ?? 0
  const confirmMessageKey =
    printJobType === 'pre-bills'
      ? 'PRE_BILLS'
      : printJobType === 'new-bills'
      ? 'NEW_BILLS'
      : printJobType === 'delinquent-bills'
      ? 'DELINQUENT_BILLS'
      : printJobType === 'by-matter'
      ? 'BY_MATTER'
      : printJobType === 'print-batch'
      ? 'PRINT_BATCH'
      : 'BILLS'
  const batchAttorneyNote =
    printJobType === 'print-batch' && batchAttorneyId
      ? ` Only bills whose matter has ${
          batchAttorneyName || 'the selected attorney'
        } as Responsible Attorney 1 are included.`
      : ''
  const trustScopeNote = printJobType === 'print-batch' ? formatTrustScopeNote(trustScope) : ''
  const deliveryScopeNote = isDelinquentSelection
    ? ` ${formatDeliveryScope(deliveryMethods, includeProformaNotes, billingTypes, paymentStatuses)}`
    : ' Only bills whose matter has a party set to Paper bill delivery are included.'
  const confirmMessage = `${
    PRINT_JOB_TYPE_DESCRIPTIONS[confirmMessageKey] ?? ''
  }${batchAttorneyNote}${trustScopeNote}${deliveryScopeNote}`
  const fullConfirmMessage = `This will queue a print job for the selected type. ${confirmMessage} The job may take some time; you can return to this page to check status and download the PDF when ready. Continue?`

  return (
    <Box>
      <ContentHead title="Print Bills" />
      <CardBox sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          New Print Job
        </Typography>
        <MuiAlert severity="info" sx={{ mb: 2 }}>
          This page prints paper bills only: a bill is included only when its matter has at least one active party whose{' '}
          <strong>Bill Delivery</strong> is set to <strong>Paper</strong>. Bills for clients on Email or SMS delivery
          are excluded. Delinquent Bills is the exception — use the delivery-method checkboxes below to widen it.
        </MuiAlert>
        <RadioGroup
          row
          value={printJobType}
          onChange={(_, v) => {
            setPrintJobType(v)
            if (v !== 'custom-attorney') {
              setCustomAttorneyId(null)
              setAttorneyRole('responsibleAttorney1')
              setCustomBillType(PrintJobType.Bills)
            }
            if (v === 'by-matter') {
              setSortBy(SORT_BY.DueDate)
            } else if (sortBy === SORT_BY.DueDate) {
              setSortBy(SORT_BY.CaseNumber)
            }
            if (v !== 'by-matter') {
              setSelectedMatterId(null)
            }
            if (v !== 'delinquent-bills' && v !== 'custom-attorney') {
              setDeliveryMethods([BillDeliveryMethod.Paper])
              setIncludeProformaNotes(false)
            }
            if (v !== 'potential-bills') {
              setPotentialAttorneyIds([])
              setPotentialCustomDate('')
              setIncludeZeroDollarBills(false)
            } else {
              // Start unrestricted: the preview should show everything that would be billed until
              // the user deliberately narrows it.
              setBillingTypes([])
              setDeliveryMethods([])
            }
            if (v === 'print-batch') {
              fetchBatches()
            } else {
              setSelectedBatchId(null)
              setBatchAttorneyId(null)
              setBatchAttorneyName('')
              setTrustScope(PrintJobTrustScope.All)
            }
          }}
          sx={{ mb: 2 }}
        >
          <FormControlLabel value="pre-bills" control={<Radio />} label="Pre Bills" />
          <FormControlLabel value="new-bills" control={<Radio />} label="New Bills" />
          <FormControlLabel value="delinquent-bills" control={<Radio />} label="Delinquent Bills" />
          <FormControlLabel value="coll1" control={<Radio />} label="Coll1" />
          <FormControlLabel value="coll2" control={<Radio />} label="Coll2" />
          <FormControlLabel value="custom-attorney" control={<Radio />} label="Custom Attorney" />
          <FormControlLabel value="by-matter" control={<Radio />} label="By Matter" />
          <FormControlLabel value="print-batch" control={<Radio />} label="Print Batch" />
          <FormControlLabel value="potential-bills" control={<Radio />} label="Bills To Be Generated" />
        </RadioGroup>
        {isDelinquentSelection && (
          <MuiAlert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              A <strong>matter</strong> is delinquent when any part of what it owes is more than{' '}
              <strong>30 days</strong> old and it still owes money overall. Age is measured from the bill the debt
              started on — not from this month&apos;s statement. When a balance goes unpaid the system issues a fresh
              statement every month and rolls the old one into it, so the latest statement is always recent (and often{' '}
              <strong>$0</strong>) even when the debt is years old.
            </Typography>
            <Typography variant="body2" component="div">
              <Box component="ul" sx={{ pl: 2.5, my: 0.5 }}>
                <li>
                  <strong>Included</strong> — $2,000 went unpaid in March. Every month since, the system has issued a $0
                  statement rolling it forward, the latest dated last week. The debt is five months old, so the matter
                  is delinquent.
                </li>
                <li>
                  <strong>Included</strong> — $500 unpaid from two months ago, plus $300 of new work this month. The
                  aged $500 qualifies it; the statement shows both, $800 due.
                </li>
                <li>
                  <strong>Not included</strong> — a bill issued three weeks ago and nothing older. Still inside the
                  30-day grace period.
                </li>
                <li>
                  <strong>Not included</strong> — an old unpaid $462, but the client also holds a $500 credit on the
                  matter. Nothing is owed on balance, so there is nothing to chase.
                </li>
                <li>
                  <strong>Not included</strong> — $4,013 from August part-paid down to $2,021, and a separate $500 from
                  August still untouched. The client is paying against the overdue debt, so &quot;Fully Unpaid&quot;
                  leaves them out. Tick &quot;Partially Unpaid&quot; to see them.
                </li>
                <li>
                  <strong>Not included</strong> — a collection matter: its Resp1 Attorney is <strong>Coll1</strong> or{' '}
                  <strong>Coll2</strong>, so the debt is already with the collection firm. To print these, choose Custom
                  Attorney, pick Coll1 or Coll2 and set the bill type to Delinquent Bills.
                </li>
              </Box>
            </Typography>
            <Typography variant="body2">
              You get <strong>one current statement per delinquent matter</strong>, listing the earlier unpaid bills it
              carries and the full amount due.
            </Typography>
          </MuiAlert>
        )}
        {isPotentialBills && (
          <>
            <MuiAlert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                Shows what the next bill run <strong>would</strong> produce for the selected attorneys &mdash; the same
                time and expense entries, discounts and totals a real run would pick up &mdash; and renders each one as
                a pre-bill. <strong>No bill is created.</strong>
              </Typography>
              <Typography variant="body2">
                Every page is stamped <strong>&ldquo;Potential bill &mdash; not an actual bill&rdquo;</strong>. The real
                bill can differ depending on when it is generated, as time and expenses are added, edited or approved.
                For attorney and paralegal information only &mdash; do not send to a client.
              </Typography>
            </MuiAlert>
            <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
              <Box sx={{ width: { xs: '100%', md: '33.333%' } }}>
                <AttorneyDropdown
                  selectedAttorneyId={potentialAttorneyIds[0] ?? undefined}
                  onChange={(value: Maybe<User>) => setPotentialAttorneyIds(value?.id ? [value.id] : [])}
                  label="Responsible Attorney 1 (required)"
                  sx={{ width: '100%' }}
                />
              </Box>
              <FormControl sx={{ minWidth: 280 }}>
                <InputLabel id="potential-cycle-label">Billing Cycle</InputLabel>
                <Select
                  labelId="potential-cycle-label"
                  value={potentialBillingCycle}
                  label="Billing Cycle"
                  onChange={e => setPotentialBillingCycle(e.target.value as BillingCycle)}
                >
                  {BILLING_CYCLE_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {potentialBillingCycle === BillingCycle.CustomDate && (
                <TextField
                  type="date"
                  label="Cutoff Date"
                  value={potentialCustomDate}
                  onChange={e => setPotentialCustomDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  error={potentialDateMissing}
                  helperText={potentialDateMissing ? 'Pick a cutoff date.' : ' '}
                />
              )}
            </Box>
            <Typography
              variant="caption"
              color={potentialAttorneyIds.length === 0 ? 'error' : 'text.secondary'}
              sx={{ display: 'block', mb: 2 }}
            >
              {potentialAttorneyIds.length === 0
                ? 'Select an attorney. This preview walks every active matter for the attorneys chosen, so one is required.'
                : 'Only matters whose Responsible Attorney 1 is the selected attorney are previewed. Contingency matters are excluded, as they are in a real bill run.'}
            </Typography>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              Include matters billed as
            </Typography>
            <FormGroup row>
              {BILLING_TYPE_OPTIONS.map(opt => (
                <FormControlLabel
                  key={opt.value}
                  control={
                    <Checkbox
                      checked={billingTypes.includes(opt.value)}
                      onChange={(_, checked) =>
                        setBillingTypes(prev => (checked ? [...prev, opt.value] : prev.filter(t => t !== opt.value)))
                      }
                    />
                  }
                  label={opt.label}
                />
              ))}
            </FormGroup>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              {billingTypes.length === 0
                ? 'No billing-type restriction: every billable matter for the attorney is previewed.'
                : `Only matters billed as ${billingTypes
                    .map(t => BILLING_TYPE_LABELS[t] ?? t)
                    .join(' or ')} are previewed.`}
            </Typography>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              Include matters delivered by
            </Typography>
            <FormGroup row>
              {DELIVERY_METHOD_OPTIONS.map(opt => (
                <FormControlLabel
                  key={opt.value}
                  control={
                    <Checkbox
                      checked={deliveryMethods.includes(opt.value)}
                      onChange={(_, checked) =>
                        setDeliveryMethods(prev => (checked ? [...prev, opt.value] : prev.filter(m => m !== opt.value)))
                      }
                    />
                  }
                  label={opt.label}
                />
              ))}
            </FormGroup>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              {deliveryMethods.length === 0
                ? 'No delivery-method restriction: this preview is for internal review, so it is not limited to paper clients.'
                : `Only matters with an active party set to ${deliveryMethods
                    .map(m => DELIVERY_METHOD_LABELS[m] ?? m)
                    .join(' or ')} delivery are previewed.`}
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeZeroDollarBills}
                  onChange={(_, checked) => setIncludeZeroDollarBills(checked)}
                />
              }
              label="Include $0 Bills with old unpaid bills"
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              {includeZeroDollarBills
                ? 'Also previews matters with no new time or expenses that still owe on an earlier bill. The scheduled monthly run raises a $0 statement for these to re-state the outstanding balance; the attorney bill run skips them.'
                : 'Matters with no new time or expenses are left out, matching the attorney bill run. Tick this to also see the $0 statements the scheduled monthly run would raise for matters that still owe.'}
            </Typography>
          </>
        )}
        {printJobType === 'custom-attorney' && (
          <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
            <Box sx={{ width: { xs: '100%', md: '33.333%' } }}>
              <AttorneyDropdown
                selectedAttorneyId={customAttorneyId ?? undefined}
                onChange={(value: Maybe<User>) => setCustomAttorneyId(value?.id ?? null)}
                label="Select Attorney"
                sx={{ width: '100%' }}
              />
            </Box>
            <FormControl sx={{ minWidth: 180 }}>
              <InputLabel id="attorney-role-label">Match by Role</InputLabel>
              <Select
                labelId="attorney-role-label"
                value={attorneyRole}
                label="Match by Role"
                onChange={e => setAttorneyRole(e.target.value)}
              >
                {ATTORNEY_ROLE_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 180 }}>
              <InputLabel id="custom-bill-type-label">Bill Type</InputLabel>
              <Select
                labelId="custom-bill-type-label"
                value={customBillType}
                label="Bill Type"
                onChange={e => setCustomBillType(e.target.value as PrintJobType)}
              >
                {CUSTOM_ATTORNEY_BILL_TYPE_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
        {isDelinquentSelection && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              Include bills delivered by
            </Typography>
            <FormGroup row>
              {DELIVERY_METHOD_OPTIONS.map(opt => (
                <FormControlLabel
                  key={opt.value}
                  control={
                    <Checkbox
                      checked={deliveryMethods.includes(opt.value)}
                      onChange={(_, checked) =>
                        setDeliveryMethods(prev => (checked ? [...prev, opt.value] : prev.filter(m => m !== opt.value)))
                      }
                    />
                  }
                  label={opt.label}
                />
              ))}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeProformaNotes}
                    onChange={(_, checked) => setIncludeProformaNotes(checked)}
                  />
                }
                label="Performa Notes"
              />
            </FormGroup>
            <Typography
              variant="caption"
              color={deliveryMethods.length === 0 ? 'error' : 'text.secondary'}
              sx={{ display: 'block' }}
            >
              {deliveryMethods.length === 0
                ? 'Select at least one delivery method.'
                : isAllDeliveryMethods(deliveryMethods)
                ? 'All three selected: every delinquent bill is printed, including matters with no bill delivery method set.'
                : `Only bills whose matter has an active party set to ${deliveryMethods
                    .map(m => DELIVERY_METHOD_LABELS[m] ?? m)
                    .join(' or ')} delivery are included.`}
              {includeProformaNotes
                ? ' Performa Notes adds the matter\u2019s billing notes to each bill, as pre-bills show them.'
                : ''}
            </Typography>
            <Typography variant="body2" fontWeight={600} gutterBottom sx={{ mt: 2 }}>
              Include matters billed as
            </Typography>
            <FormGroup row>
              {BILLING_TYPE_OPTIONS.map(opt => (
                <FormControlLabel
                  key={opt.value}
                  control={
                    <Checkbox
                      checked={billingTypes.includes(opt.value)}
                      onChange={(_, checked) =>
                        setBillingTypes(prev => (checked ? [...prev, opt.value] : prev.filter(t => t !== opt.value)))
                      }
                    />
                  }
                  label={opt.label}
                />
              ))}
            </FormGroup>
            <Typography
              variant="caption"
              color={billingTypes.length === 0 ? 'error' : 'text.secondary'}
              sx={{ display: 'block' }}
            >
              {billingTypes.length === 0
                ? 'Select at least one billing type.'
                : isAllBillingTypes(billingTypes)
                ? 'All billing types selected: no billing-type restriction is applied.'
                : `Only delinquent matters billed as ${billingTypes
                    .map(t => BILLING_TYPE_LABELS[t] ?? t)
                    .join(' or ')} are included.`}
            </Typography>
            <Typography variant="body2" fontWeight={600} gutterBottom sx={{ mt: 2 }}>
              Include matters whose aged bills are
            </Typography>
            <FormGroup row>
              {PAYMENT_STATUS_OPTIONS.map(opt => (
                <FormControlLabel
                  key={opt.value}
                  control={
                    <Checkbox
                      checked={paymentStatuses.includes(opt.value)}
                      onChange={(_, checked) =>
                        setPaymentStatuses(prev => (checked ? [...prev, opt.value] : prev.filter(p => p !== opt.value)))
                      }
                    />
                  }
                  label={opt.label}
                />
              ))}
            </FormGroup>
            <Typography
              variant="caption"
              color={paymentStatuses.length === 0 ? 'error' : 'text.secondary'}
              sx={{ display: 'block' }}
            >
              {paymentStatuses.length === 0
                ? 'Select at least one payment status.'
                : isAllPaymentStatuses(paymentStatuses)
                ? 'Both selected: every delinquent matter is printed, whether or not the client has paid something.'
                : paymentStatuses.includes(DelinquentPaymentStatus.FullyUnpaid)
                ? 'Only matters that have paid nothing at all against their aged bills. A client who has put money against any overdue bill is engaging and is left out, even if a newer statement is still untouched.'
                : 'Only matters that have paid something against their aged bills and still owe. Matters that have paid nothing at all are left out.'}
            </Typography>
          </Box>
        )}
        {printJobType === 'by-matter' && (
          <Box sx={{ mb: 2, maxWidth: 400 }}>
            <MatterDropdown
              label="Select Matter"
              customOnChange={(value: Maybe<Case>) => setSelectedMatterId(value?.id ?? null)}
            />
          </Box>
        )}
        {printJobType === 'print-batch' && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
              <FormControl size="small" sx={{ width: { xs: '100%', md: 400 } }}>
                <InputLabel id="print-batch-select-label">Select Batch</InputLabel>
                <Select
                  labelId="print-batch-select-label"
                  value={selectedBatchId ?? ''}
                  label="Select Batch"
                  onChange={e => setSelectedBatchId(Number(e.target.value))}
                  disabled={batchesLoading}
                >
                  {batchesLoading && <MenuItem value="">Loading...</MenuItem>}
                  {(batchesData?.getBillEmailBatches?.batches ?? []).map(b => (
                    <MenuItem key={b.id} value={b.id}>
                      {b.billingCycle} — {b.periodMonth}/{b.periodYear}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {/* Optional second filter: keeps only the batch's bills whose matter has this
                  attorney as Responsible Attorney 1. Cleared with the field's X. */}
              <AttorneyDropdown
                selectedAttorneyId={batchAttorneyId ?? undefined}
                onChange={(value: Maybe<User>) => {
                  setBatchAttorneyId(value?.id ?? null)
                  setBatchAttorneyName(`${value?.firstName ?? ''} ${value?.lastName ?? ''}`.trim())
                }}
                label="Resp. Attorney 1 (optional)"
                size="small"
                sx={{ width: { xs: '100%', md: 300 } }}
              />
            </Box>
            {/* Trust filter. Uses the same test as the "Bills with Trust Money" checkbox on the
                bills list, so the two screens always agree on which matters hold trust. */}
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" component="div">
                Trust money
              </Typography>
              <RadioGroup row value={trustScope} onChange={(_, v) => setTrustScope(v as PrintJobTrustScope)}>
                {TRUST_SCOPE_OPTIONS.map(o => (
                  <FormControlLabel key={o.value} value={o.value} control={<Radio size="small" />} label={o.label} />
                ))}
              </RadioGroup>
              <Typography variant="caption" color="text.secondary" component="div">
                A matter counts as having trust money when its trust balance is above zero right now — the same rule as
                the <strong>Bills with Trust Money</strong> filter on the Bills page.
              </Typography>
            </Box>
            {selectedBatchId && (
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, minHeight: 28 }}>
                {paperItemsLoading ? (
                  <CircularProgress size={14} />
                ) : paperItemsError ? (
                  <Typography variant="caption" color="error">
                    Failed to load batch items
                  </Typography>
                ) : !paperItemsLoading && selectedBatchId && paperItemsData ? (
                  paperItems.length === 0 ? (
                    <Typography variant="caption" color="warning.main">
                      {batchAttorneyId || trustScope !== PrintJobTrustScope.All
                        ? 'No paper items in this batch match the selected filters'
                        : 'No paper items in this batch'}
                    </Typography>
                  ) : (
                    <>
                      <Typography variant="caption" color="text.secondary">
                        {activePaperCount} bill(s) will be printed
                        {excludedCount > 0 ? `, ${excludedCount} excluded` : ''}
                      </Typography>
                      <Typography
                        sx={{
                          textDecoration: 'underline',
                          cursor: 'pointer'
                        }}
                        onClick={() => setPreviewOpen(true)}
                      >
                        Preview Bills
                      </Typography>
                    </>
                  )
                ) : null}
              </Box>
            )}
          </Box>
        )}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="print-job-sort-by-label">Sort by</InputLabel>
            <Select
              labelId="print-job-sort-by-label"
              value={sortBy}
              label="Sort by"
              onChange={e => setSortBy(e.target.value)}
            >
              <MenuItem value={SORT_BY.CaseNumber}>Case number</MenuItem>
              <MenuItem value={SORT_BY.Attorney}>Attorney name</MenuItem>
              <MenuItem value={SORT_BY.DueDate}>Due date</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="print-job-sort-dir-label">Direction</InputLabel>
            <Select
              labelId="print-job-sort-dir-label"
              value={sortDir}
              label="Direction"
              onChange={e => setSortDir(e.target.value)}
            >
              <MenuItem value={SORT_DIR.Asc}>Ascending</MenuItem>
              <MenuItem value={SORT_DIR.Desc}>Descending</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Button
          variant="contained"
          onClick={() => setConfirmOpen(true)}
          disabled={
            creating ||
            (printJobType === 'custom-attorney' && !customAttorneyId) ||
            (isPotentialBills && potentialAttorneyIds.length === 0) ||
            potentialDateMissing ||
            (isDelinquentSelection && deliveryMethods.length === 0) ||
            (isDelinquentSelection && billingTypes.length === 0) ||
            (isDelinquentSelection && paymentStatuses.length === 0) ||
            (printJobType === 'by-matter' && !selectedMatterId) ||
            (printJobType === 'print-batch' && !selectedBatchId) ||
            (printJobType === 'print-batch' &&
              !!selectedBatchId &&
              !paperItemsLoading &&
              !!paperItemsData &&
              activePaperCount === 0)
          }
        >
          {creating ? 'Submitting...' : 'Generate Print Job'}
        </Button>
      </CardBox>

      <CardBox sx={{ p: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
            p: 2
          }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            Print Job History
          </Typography>
          <ToggleButtonGroup
            value={statusFilter}
            exclusive
            onChange={(_, value: PrintJobStatusFilter | null) => {
              if (value != null) {
                setStatusFilter(value)
                setPage(1)
              }
            }}
            size="small"
            aria-label="Filter by status"
          >
            <ToggleButton value="in_progress_completed" aria-label="In progress & completed" sx={{ p: 1.5 }}>
              In progress & completed
            </ToggleButton>
            <ToggleButton value="all" aria-label="All" sx={{ p: 1.5 }}>
              All
            </ToggleButton>
            <ToggleButton value="failed" aria-label="Failed" sx={{ p: 1.5 }}>
              Failed
            </ToggleButton>
            <ToggleButton value="cancelled" aria-label="Canceled" sx={{ p: 1.5 }}>
              Canceled
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        {loading && !printJobs.length ? (
          <Box sx={{ p: 2 }}>Loading...</Box>
        ) : !printJobs.length ? (
          <EmptyData title="No print jobs yet." height="400" />
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Requested By</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Attorney</TableCell>
                    <TableCell>Sort/Order</TableCell>
                    <TableCell>Total Bills</TableCell>
                    <TableCell>Progress</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Time</TableCell>
                    <TableCell>Sent to Client</TableCell>
                    <TableCell>Sent At</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {printJobs.map(pj => {
                    if (!pj) return null
                    const status = pj.status ?? ''
                    const statusColor = PRINT_JOB_STATUS_COLORS[status] ?? '#9E9E9E'
                    const isCompleted = status === 'COMPLETED'
                    const progressStr =
                      pj.totalBills != null && pj.processedBills != null
                        ? `${pj.processedBills} / ${pj.totalBills}`
                        : '—'
                    const isTerminal = ['COMPLETED', 'FAILED', 'CANCELLED'].includes(status)
                    const updatedAt = (pj as { updatedAt?: string }).updatedAt
                    const durationStr =
                      isTerminal && pj.createdAt && updatedAt
                        ? formatDurationMs(new Date(updatedAt).getTime() - new Date(pj.createdAt).getTime())
                        : '—'
                    return (
                      <TableRow key={pj.id}>
                        <TableCell>{formatDateTime(pj.createdAt)}</TableCell>
                        <TableCell>
                          {pj.creator
                            ? `${pj.creator.firstName ?? ''} ${pj.creator.lastName ?? ''}`.trim() || pj.creator.email
                            : '—'}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                            {getPrintJobTypeLabel(pj.printJobType, pj.attorneyIds)}
                            {/* Two Delinquent jobs can print very different sets, so name the scope. */}
                            {(() => {
                              const jobMethods = ((pj as { deliveryMethods?: BillDeliveryMethod[] | null })
                                .deliveryMethods ?? []) as BillDeliveryMethod[]
                              const jobNotes =
                                (pj as { includeProformaNotes?: boolean | null }).includeProformaNotes === true
                              const jobTypes = ((pj as { billingTypes?: BillingType[] | null }).billingTypes ??
                                []) as BillingType[]
                              const jobPayment = ((pj as { paymentStatuses?: DelinquentPaymentStatus[] | null })
                                .paymentStatuses ?? []) as DelinquentPaymentStatus[]
                              if (!jobMethods.length && !jobNotes && !jobTypes.length && !jobPayment.length) return null
                              const scope = isAllDeliveryMethods(jobMethods)
                                ? 'All methods'
                                : jobMethods.map(m => DELIVERY_METHOD_LABELS[m] ?? m).join(', ')
                              // Jobs created before the billing-type filter have none recorded, and
                              // printed every type — leave the caption unchanged for those.
                              const typeScope = !jobTypes.length
                                ? ''
                                : isAllBillingTypes(jobTypes)
                                ? 'All billing types'
                                : jobTypes.map(t => BILLING_TYPE_LABELS[t] ?? t).join(', ')
                              // Jobs created before the payment-status filter have none recorded, and
                              // printed both — leave the caption unchanged for those.
                              const paymentScope =
                                !jobPayment.length || isAllPaymentStatuses(jobPayment)
                                  ? ''
                                  : jobPayment.map(p => PAYMENT_STATUS_LABELS[p] ?? p).join(', ')
                              return (
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                  (
                                  {[scope, typeScope, paymentScope, jobNotes ? 'notes' : '']
                                    .filter(Boolean)
                                    .join(' + ')}
                                  )
                                </Typography>
                              )
                            })()}
                            {/* Two batch jobs on the same batch can print very different sets once
                                the trust filter is used, so name it. Jobs predating the filter (and
                                unfiltered ones) record nothing and keep their old caption. */}
                            {(() => {
                              const jobTrustScope = (pj as { trustScope?: PrintJobTrustScope | null }).trustScope
                              if (!jobTrustScope || jobTrustScope === PrintJobTrustScope.All) return null
                              return (
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                  ({TRUST_SCOPE_LABELS[jobTrustScope] ?? jobTrustScope})
                                </Typography>
                              )
                            })()}
                            {PRINT_JOB_TYPE_DESCRIPTIONS[pj.printJobType ?? ''] && (
                              <Tooltip
                                title={
                                  <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {PRINT_JOB_TYPE_DESCRIPTIONS[pj.printJobType ?? '']}
                                  </Typography>
                                }
                                placement="top"
                                arrow
                              >
                                <IconButton size="small" sx={{ p: 0.25 }} aria-label="Type details">
                                  <InfoIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const attorneys = (
                              pj as {
                                attorneys?: Array<{ firstName?: string | null; lastName?: string | null }> | null
                              }
                            ).attorneys
                            return attorneys?.length
                              ? attorneys.map(a => `${a?.firstName ?? ''} ${a?.lastName ?? ''}`.trim()).join(', ')
                              : '—'
                          })()}
                        </TableCell>
                        <TableCell>
                          {formatSortOrder(
                            (pj as { sortBy?: string | null }).sortBy,
                            (pj as { sortDir?: string | null }).sortDir
                          )}
                        </TableCell>
                        <TableCell>{pj.totalBills ?? '—'}</TableCell>
                        <TableCell>{progressStr}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                            <Box
                              component="span"
                              sx={{
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                                fontSize: 12,
                                color: statusColor,
                                border: `1px solid ${statusColor}50`,
                                backgroundColor: 'transparent'
                              }}
                            >
                              {status}
                            </Box>
                            {status === 'FAILED' && (
                              <Tooltip title="View failure reason">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    setFailedErrorDialog({
                                      message:
                                        pj.errorMessage ||
                                        (pj.skippedBills?.length
                                          ? `${pj.skippedBills.length} bill(s) skipped.`
                                          : 'No error details available.')
                                    })
                                  }
                                  aria-label="View failure reason"
                                  sx={{ color: 'error.main' }}
                                >
                                  <InfoIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                          {pj.skippedBills?.length && status !== 'FAILED' ? (
                            <Typography variant="caption" display="block" color="warning.main">
                              {pj.skippedBills.length} bill(s) skipped
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>{durationStr}</TableCell>
                        <TableCell>
                          {(() => {
                            // Reflects the real state of the job's bills: "Yes" once every bill in the
                            // batch has been sent to the client (backend resolver `sentToClientAt`).
                            const billsSent = !!pj.sentToClientAt
                            const sentColor = billsSent ? statusColors[BillStatus.SentToClient] : '#9E9E9E'
                            return (
                              <Box
                                component="span"
                                sx={{
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1,
                                  fontSize: 12,
                                  color: sentColor,
                                  border: `1px solid ${sentColor}50`,
                                  backgroundColor: 'transparent'
                                }}
                              >
                                {billsSent ? 'Yes' : 'No'}
                              </Box>
                            )
                          })()}
                        </TableCell>
                        <TableCell>{pj.sentToClientAt ? formatDateTime(pj.sentToClientAt) : '—'}</TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={e => {
                              setActionMenuAnchor(e.currentTarget)
                              setActionMenuJobId(pj.id)
                            }}
                            aria-label="Actions"
                            disabled={
                              !(
                                status === 'REQUESTED' ||
                                status === 'IN_PROGRESS' ||
                                isCompleted ||
                                status === 'CANCELLED'
                              )
                            }
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                          <Menu
                            anchorEl={actionMenuAnchor}
                            open={Boolean(actionMenuAnchor) && actionMenuJobId === pj.id}
                            onClose={() => {
                              setActionMenuAnchor(null)
                              setActionMenuJobId(null)
                            }}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                          >
                            {(status === 'REQUESTED' || status === 'IN_PROGRESS') && (
                              <MenuItem
                                onClick={() => {
                                  setActionMenuAnchor(null)
                                  setActionMenuJobId(null)
                                  refetch?.()
                                }}
                                disabled={loading}
                              >
                                <RefreshIcon fontSize="small" sx={{ mr: 1 }} /> Refresh status
                              </MenuItem>
                            )}
                            {(status === 'REQUESTED' || status === 'IN_PROGRESS') && (
                              <MenuItem
                                onClick={() => handleCancelClick(pj.id)}
                                disabled={cancelling}
                                sx={{ color: 'error.main' }}
                              >
                                Cancel job
                              </MenuItem>
                            )}
                            {status === 'CANCELLED' && (
                              <MenuItem onClick={() => handleRetryClick(pj.id)} disabled={retrying}>
                                <RefreshIcon fontSize="small" sx={{ mr: 1 }} /> Retry job
                              </MenuItem>
                            )}
                            {isCompleted && (
                              <MenuItem
                                onClick={() => {
                                  setActionMenuAnchor(null)
                                  setActionMenuJobId(null)
                                  const date = pj.createdAt ? new Date(pj.createdAt) : new Date()
                                  pendingDownloadFilenameRef.current = getPrintJobDownloadFilename(
                                    pj.printJobType ?? '',
                                    date
                                  )
                                  downloadPrintJob({ variables: { id: pj.id } })
                                }}
                                disabled={downloading}
                              >
                                Download PDF
                              </MenuItem>
                            )}
                            {isCompleted && (pj.totalBills ?? 0) > 0 && (
                              <MenuItem onClick={() => handleMarkDeliverClick(pj.id)} disabled={markingDelivered}>
                                Mark bills as delivered
                              </MenuItem>
                            )}
                          </Menu>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {totalPages > 1 && (
              <PaginationWithJump count={totalPages} page={page} onChange={setPage} sx={{ pt: 2, pb: 2 }} />
            )}
          </>
        )}
      </CardBox>

      <ConfirmationModal
        isOpen={confirmOpen}
        title="Generate Print Job"
        message={fullConfirmMessage}
        onConfirm={handleRequestPrint}
        onCancel={() => setConfirmOpen(false)}
        isLoading={creating}
        variant="danger"
      />

      <ConfirmationModal
        isOpen={cancelConfirmOpen}
        title="Cancel Print Job"
        message="Are you sure you want to cancel this print job? It will not be processed."
        onConfirm={handleCancelConfirm}
        onCancel={() => {
          setCancelConfirmOpen(false)
          setCancelJobId(null)
        }}
        isLoading={cancelling}
        variant="danger"
      />

      <ConfirmationModal
        isOpen={retryConfirmOpen}
        title="Retry Print Job"
        message="This will re-queue the cancelled print job with the same settings. It will be processed again and a fresh PDF generated. Continue?"
        confirmText="Retry"
        onConfirm={handleRetryConfirm}
        onCancel={() => {
          setRetryConfirmOpen(false)
          setRetryJobId(null)
        }}
        isLoading={retrying}
        variant="danger"
      />

      <ConfirmationModal
        isOpen={markDeliverConfirmOpen}
        title="Mark Bills as Delivered"
        message={
          'Confirm that the bills in this print batch have been delivered to the client.\n\n' +
          'When you do this, every bill in this batch is marked as sent to the client - its "Sent to Client" ' +
          'flag is set to Yes and the sent date is recorded. This cannot be undone.'
        }
        confirmText="Mark as Delivered"
        onConfirm={handleMarkDeliverConfirm}
        onCancel={() => {
          setMarkDeliverConfirmOpen(false)
          setMarkDeliverJobId(null)
        }}
        isLoading={markingDelivered}
        variant="danger"
      />

      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          style: {
            margin: 0,
            width: 'calc(100% - 30px)'
          }
        }}
      >
        <Box sx={{ borderBottom: '1px solid', borderColor: 'lightGray' }}>
          <DialogTitle>
            Paper Items
            {paperBatch && (
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                — {paperBatch.billingCycle} · {paperBatch.periodMonth}/{paperBatch.periodYear} · Batch #{paperBatch.id}
                {batchAttorneyId ? ` · Resp. Attorney 1: ${batchAttorneyName || '—'}` : ''}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent dividers sx={{ p: 0 }}>
            {paperItemsLoading ? (
              <Box display="flex" justifyContent="center" py={5}>
                <CircularProgress />
              </Box>
            ) : paperItemsError ? (
              <Box p={3}>
                <Typography color="error" variant="body2">
                  Failed to load batch items. Please close and try again.
                </Typography>
              </Box>
            ) : paperItems.length === 0 ? (
              <Box p={4} textAlign="center">
                <Typography variant="body2" color="text.secondary">
                  No paper items in this batch.
                </Typography>
              </Box>
            ) : (
              <TableContainer sx={{ maxHeight: 480 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Matter #</TableCell>
                      <TableCell>Matter Name</TableCell>
                      <TableCell>Bill Status</TableCell>
                      <TableCell>Due Date</TableCell>
                      <TableCell>Item Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...paperItems]
                      .sort((a, b) => {
                        const aNum = parseInt(a.bill?.case?.caseNumber ?? '0', 10)
                        const bNum = parseInt(b.bill?.case?.caseNumber ?? '0', 10)
                        return bNum - aNum
                      })
                      .map(item => {
                        const isExcluded = item.status === 'EXCLUDED'
                        return (
                          <TableRow key={item.id} hover sx={{ opacity: isExcluded ? 0.45 : 1 }}>
                            <TableCell>
                              {!!item.bill?.case?.id && (
                                <Link
                                  to={`/matters/matter-detail/${item.bill?.case?.id}`}
                                  target="_blank"
                                  style={{
                                    color: palette.primary.main,
                                    textDecoration: 'none'
                                  }}
                                >
                                  #{item.bill?.case?.caseNumber ?? '—'}
                                </Link>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">{item.bill?.case?.title ?? '—'}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary">
                                {item.bill?.status ?? '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary">
                                {item.bill?.dueDate ? new Date(item.bill.dueDate).toLocaleDateString() : '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={isExcluded ? 'Excluded' : 'Included'}
                                size="small"
                                sx={{
                                  fontSize: '0.7rem',
                                  color: isExcluded ? '#9E9E9E' : '#2E7D32',
                                  border: `1px solid ${isExcluded ? '#9E9E9E50' : '#2E7D3250'}`,
                                  backgroundColor: 'transparent'
                                }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              {isExcluded ? (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: '#2E7D32',
                                    cursor: toggling ? 'not-allowed' : 'pointer',
                                    opacity: toggling ? 0.5 : 1
                                  }}
                                  onClick={() =>
                                    !toggling && toggleExclusion({ variables: { input: { itemId: item.id } } })
                                  }
                                >
                                  Re-enable
                                </Typography>
                              ) : (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: '#D32F2F',
                                    cursor: toggling ? 'not-allowed' : 'pointer',
                                    opacity: toggling ? 0.5 : 1
                                  }}
                                  onClick={() =>
                                    !toggling && toggleExclusion({ variables: { input: { itemId: item.id } } })
                                  }
                                >
                                  Exclude
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        )
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto', pl: 1 }}>
              {paperItemsLoading ? 'Loading…' : `${activePaperCount} will print · ${excludedCount} excluded`}
            </Typography>
            <Button variant="outlined" onClick={() => setPreviewOpen(false)}>
              Close
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={Boolean(failedErrorDialog)} onClose={() => setFailedErrorDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Failure reason</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {failedErrorDialog?.message ?? ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFailedErrorDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default PrintJobs
